import React, { useEffect, useState } from "react";
import * as API from "src/api/high_risk";
import List from "src/pages/high_risk/register/list";
import Header from "src/pages/high_risk/register/header";
import {
  Spin,
  Modal,
  InputNumber,
  Input,
  Select,
  Space,
  Button,
  Tooltip,
} from "antd";
import { FilterMatchMode } from "primereact/api";
import Swal from "sweetalert2";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import MODAL from "src/pages/high_risk/register/modal";
import MODAL_APPROVE from "src/pages/high_risk/register/modal_approve";
import moment from "moment";
// Биоритмын сэрэмжлүүлэг — ажилтны нэрийн ард жижиг тэмдэг.
// Backend `biorhythm`-ыг эрхээр нь шүүж өгдөг (Utils/biorhythm.can_view):
// эрхгүй хэрэглэгчид `null` ирнэ → тэмдэг огт харагдахгүй.
import BiorhythmMark from "src/components/BiorhythmMark";
import { useUserContext } from "src/contexts/userContext";
import { useHighRiskContext } from "src/contexts/high_risk/highriskContext";
import _ from "lodash";
import { SearchInput } from "src/components/filters";
import { bnKeyOf, isSectionCode, isUnderUnit } from "src/utils/unitCode";

/**
 * §4-ийн дүр бүрийн ӨНГӨ — `SignatureSection.js`-ийн PARTY_COLOR-той ИЖИЛ.
 *
 * ⚠ Хоёр газар ижил өнгөтэй байх нь чухал: хэрэглэгч жагсаалтаас
 *   «улбар шар мөр дутуу байна» гэж хараад наряд нээхэд ЯГ тэр өнгөт
 *   хэсэг рүү нүд нь очих ёстой. Өнгө зөрвөл холбоос тасарна.
 *
 * `bg`/`fg` нь ГАРЫН ҮСЭГ ДУТУУ үед хэрэглэгдэх дэвсгэр/үсгийн өнгө.
 * Зөвхөн үсгийн өнгөөр (`text-amber-600` гэх мэт) ялгах нь цайвар
 * дэвсгэр дээр ЯЛГАРАХГҮЙ байсан тул дэвсгэртэй болгов.
 */
const PARTY_TONE = {
  client: { bar: "#0EA5E9", label: "Ажилд оруулагч" },
  admin: { bar: "#7C3AED", label: "Хариуцлагатай удирдагч" },
  performer: { bar: "#B45309", label: "Ажил гүйцэтгэгч" },
  licenser: { bar: "#DC2626", label: "Зөвшөөрөл олгогч" },
  hab: { bar: "#16A34A", label: "ХАБЭА-н инженер" },
};

// Дүрийг маягтын дараалалаар — §4.1 → §4.5
const PARTY_SEQ = ["client", "admin", "performer", "licenser", "hab"];

// Гарын үсэг ДУУССАН / ДУТУУ хоёрын өнгө. Дутуу нь ТОД шар дэвсгэртэй —
// цонхийсон үсгийн өнгө нь олон мөрийн дунд анзаарагдахгүй байв.
const SIGN_DONE = { bg: "#DCFCE7", fg: "#15803D" };
const SIGN_WAIT = { bg: "#FDE68A", fg: "#92400E" };

// Хяналтын нэг KPI карт
/**
 * KpiTile — нэг тоон үзүүлэлт.
 *
 * ⚠ `onClick` өгвөл ДАРЖ ШҮҮНЭ. Тоо харуулаад орхивол хэрэглэгч «12
 *   наряд гацсан байна» гэдгийг мэдэх ч ЯМАР 12-ыг нь олохын тулд
 *   шүүлтээ гараар тааруулах хэрэгтэй болно. Тоо ба жагсаалт хоёрыг
 *   холбосноор нэг товшилтоор шилжинэ.
 *
 * ⚠ Идэвхтэй үед хүрээ нь ӨТГӨРНӨ — аль шүүлт асаалттай байгааг
 *   хэрэглэгч мартвал «жагсаалт хоосон байна» гэж эргэлзэнэ.
 */
const KpiTile = ({ label, value, sub, color, icon, onClick, active }) => (
  <div
    className={
      "flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border bg-white" +
      (onClick ? " cursor-pointer" : "")
    }
    onClick={onClick}
    title={onClick ? "Дарж шүүх" : undefined}
    style={{
      minWidth: 120,
      borderColor: active ? color : color + "44",
      borderWidth: active ? 2 : 1,
      boxShadow: active ? `0 0 0 3px ${color}1A` : undefined,
    }}
  >
    <div
      className="flex items-center justify-center rounded-md"
      style={{ width: 30, height: 30, background: color + "18", color }}
    >
      <i className={"fas " + icon} />
    </div>
    <div className="leading-tight">
      <div className="text-[16px] font-bold" style={{ color }}>
        {value}
      </div>
      <div className="text-[10px] text-gray-500">
        {label}
        {sub ? ` · ${sub}` : ""}
      </div>
    </div>
  </div>
);

const RegisterList = () => {
  const { user, message, checkRole, isAdmin } = useUserContext();
  const { state, dispatch } = useHighRiskContext();

  // Бүрэн эрх (sso_owner) — цех/бүтцийн нэгжээр групплэж харуулна
  const admin = isAdmin();

  const [search, setSearch] = useState({
    global: { value: "", matchMode: FilterMatchMode.CONTAINS },
  });

  const [loading, setLoading] = useState(false);

  const [first, set_first] = useState(0);
  const [per_page, set_per_page] = useState(50);
  // Задардаг мөр — тухайн ажилд бүртгэгдсэн ажилчид
  // ⚠ МАССИВ — доорх DataTable-ийн тайлбарыг үзнэ үү (dataKey хасагдсан)
  const [expandedRows, setExpandedRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  // ── ХЭРЭГЛЭГЧТЭЙ ХОЛБООТОЙ ШҮҮЛТ ────────────────────────────────────
  //   ""        — бүгд
  //   "mine"    — би §4-т томилогдсон эсвэл §5/§6-д гарын үсэг зурсан
  //   "waiting" — МИНИЙ гарын үсэг хүлээгдэж байгаа наряд
  const [userFilter, setUserFilter] = useState("");
  // Тодорхой хүнээр — нэр эсвэл Б/Д-аар
  const [personQ, setPersonQ] = useState("");
  // Аль шат дээр гацсанаар нь
  const [stageFilter, setStageFilter] = useState("");

  const [list, setList] = useState();
  const [group_list, setgroup_list] = useState([]);
  const [type, setType] = useState();

  const [factcount, setFactcount] = useState(0);
  useEffect(() => {
    // state.department нь null/undefined бол DepartmentTreeCode-ийн async
    // auto-select хүлээгдэж байгаа — API огт дуудахгүй, харин list-ийг бас
    // тэг болгож "хоосон" харуулахгүй (хүлээх төлөв).
    if (state.department == null) return;

    // "Бүгд" (0 / "0" / "") — dep_code-г огт явуулахгүй: backend нь
    // dep_code байхгүй бол бүх нэгжийн бичлэгийг буцаана.
    const params = { year: moment(state.end_date || moment()).format("YYYY") };
    if (
      state.department !== 0 &&
      state.department !== "0" &&
      state.department !== ""
    ) {
      params.dep_code = state.department;
    }

    API.getRegister(params)
      .then((res) => {
        setList(Array.isArray(res) ? res : []);
      })
      .catch((error) => {
        message({
          type: "error",
          error,
          title: "Эрсдлийн үнэлгээний татахад алдаа гарлаа",
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.refresh, state.department, state.end_date]);

  /* ⚠ ДАРААЛАЛ: `tnKey` · `myKey` · `waitingForMe` нь KPI-аас ДЭЭР байх
     ЁСТОЙ. `const` нь hoist болдоггүй тул доор тодорхойлвол `kpiBase`
     дотроос уншихад «Cannot access 'myKey' before initialization» гэж
     рендер бүтэн унана (2026-09-26). */
  /** ТН жигдрүүлэх — `017824` ба `17824` нь НЭГ хүн.
   *
   *  ⚠ Backend `stage.actor_tns`-ийг аль хэдийн жигдрүүлж өгдөг
   *    (Models/highrisk/stage_progress.py · tn_digits). Энд нэвтэрсэн
   *    хэрэглэгчийн ТН-г ЯГ ижил дүрмээр цэвэрлэнэ — эс бөгөөс «миний
   *    наряд» шүүлт чимээгүй хоосон гарч, хэрэглэгч ажилгүй мэт харна. */
  const tnKey = React.useCallback(
    (v) => String(v || "").replace(/[^0-9]/g, "").replace(/^0+/, ""),
    [],
  );
  const myKey = React.useMemo(
    () => tnKey(user?.tn || user?.info?.tn),
    [tnKey, user],
  );

  /** Тухайн наряд дээр ЭНЭ ТН гарын үсэг ХҮЛЭЭГДЭЖ байна уу.
   *  §4-т томилогдсон мөртлөө `signed` нь `count`-оос дутуу дүр бий эсэх. */
  const waitingForMe = React.useCallback(
    (j) => {
      const st = j?.stage;
      if (!st || !myKey) return false;
      if (!(st.actor_tns || []).includes(myKey)) return false;
      // Бүх шат дууссан бол хүлээх зүйл алга
      return st.stage === "parties" || st.stage === "signed"
        ? Object.values(st.parties || {}).some(
            (p) => p.count > 0 && p.signed < p.count,
          )
        : false;
    },
    [myKey],
  );

  /* ── Хяналтын (KPI) тоон үзүүлэлт ───────────────────────────────────
     ⚠ ХАЙЛТЫН ХЭСЭГТЭЭ БҮРЭН ХАМААРНА (2026-09-26): өмнө нь бүх мөрөөс
       (`list`) бодогддог байсан тул хэрэглэгч нэгж/төлөвөөр шүүхэд
       хүснэгт өөрчлөгдөж, KPI хуучин тоогоо хэвээр харуулдаг байв —
       хоёр тоо зөрж «аль нь үнэн бэ» гэсэн эргэлзээ төрүүлнэ.
     ⚠ ШАТНЫ шүүлтийг ОРУУЛААГҮЙ (`kpiBase`): шатны карт нь өөрөө
       шүүлтийн товч тул өөрийгөө 0 болгох ёсгүй — эс бөгөөс буцаж
       сонгох боломжгүй болно. */
  const kpiBase = React.useMemo(() => {
    let rows = Array.isArray(list) ? list : [];
    if (state.section) {
      rows = rows.filter((j) =>
        isUnderUnit(j.negj_code || j.department_code, state.section),
      );
    }
    if (statusFilter === "approved") rows = rows.filter((j) => j.approve_id === 41);
    else if (statusFilter === "pending") rows = rows.filter((j) => j.approve_id == null);
    else if (statusFilter === "rejected")
      rows = rows.filter((j) => j.approve_id != null && j.approve_id !== 41);
    if (userFilter === "mine" && myKey)
      rows = rows.filter((j) => (j.stage?.actor_tns || []).includes(myKey));
    else if (userFilter === "waiting") rows = rows.filter(waitingForMe);
    return rows;
  }, [list, state.section, statusFilter, userFilter, myKey, waitingForMe]);

  const kpi = React.useMemo(() => {
    const rows = kpiBase;
    let workers = 0;
    let done15 = 0;
    let hazardWorkers = 0;
    rows.forEach((j) => {
      const ws = j.workers || [];
      workers += ws.length;
      ws.forEach((w) => {
        if (w.checklist_15_completed) done15 += 1;
        if (w.hazard_reported) hazardWorkers += 1;
      });
    });
    return {
      jobs: rows.length,
      workers,
      done15,
      pct15: workers ? Math.round((done15 / workers) * 100) : 0,
      hazardWorkers,
      // Зөвшөөрөл хүлээгдэж буй — удирдлагын хамгийн эхний шийдвэр
      pending: rows.filter((j) => j.approve_id == null).length,
      approved: rows.filter((j) => j.approve_id === 41).length,
    };
  }, [kpiBase]);

  /**
   * ── ШАТНЫ ЯВЦААС ГАРАХ ТООН ҮЗҮҮЛЭЛТ ────────────────────────────────
   *
   * ⚠ Эдгээр нь `kpiBase`-аас тооцогдоно — өөрөөр хэлбэл ХАЙЛТЫН хэсэгт
   *   (нэгж · хэсэг · төлөв · хэрэглэгч) БҮРЭН хамаарна, гэхдээ ШАТНЫ
   *   шүүлтээс хамаарахгүй. Шатны карт нь өөрөө шүүлтийн товч тул
   *   өөрийгөө 0 болговол буцаж сонгох боломжгүй болно.
   *
   * ⚠ `stage` талбаргүй мөрийг (migration ажиллаагүй суулгац) бүх
   *   тоололоос ГАДУУР үлдээнэ — «0%» гэж тооцвол дундаж явц худал
   *   доошилно.
   */
  const stageKpi = React.useMemo(() => {
    const rows = kpiBase.filter((j) => j.stage);
    if (!rows.length) {
      return {
        has: false,
        avg: 0,
        done: 0,
        signWait: 0,
        shiftOpen: 0,
        mineWait: 0,
        overdue: 0,
      };
    }

    const today = moment().startOf("day");
    let sum = 0;
    let done = 0;
    let signWait = 0;
    let shiftOpen = 0;
    let mineWait = 0;
    let overdue = 0;

    rows.forEach((j) => {
      const st = j.stage;
      sum += st.percent ?? 0;
      if (st.stage === "done") done += 1;
      // §4 дээр гацсан — ИТА томилоогүй эсвэл гарын үсэг дутуу
      if (st.stage === "parties" || st.stage === "signed") signWait += 1;
      // ⚠ ЯГ ОДОО ажил явагдаж байгаа газрууд — нээгдсэн ч хаагдаагүй
      //   ээлж. Хяналтын хувьд хамгийн эмзэг тоо.
      if ((st.shifts?.opened || 0) > (st.shifts?.closed || 0)) shiftOpen += 1;
      if (waitingForMe(j)) mineWait += 1;
      // Хугацаа дууссан ч дуусгаагүй наряд
      if (
        j.end_date &&
        st.stage !== "done" &&
        moment(j.end_date).startOf("day").isBefore(today)
      ) {
        overdue += 1;
      }
    });

    return {
      has: true,
      avg: Math.round(sum / rows.length),
      done,
      signWait,
      shiftOpen,
      mineWait,
      overdue,
      total: rows.length,
    };
  }, [kpiBase, waitingForMe]);

  const filteredList = React.useMemo(() => {
    let rows = Array.isArray(list) ? list : [];

    /* ── ХЭСЭГ / НЭГЖ (2026-09-26) ────────────────────────────────────
       БН-ийг backend шүүдэг (`state.department` → хүсэлтийн параметр),
       харин ХЭСГИЙГ энд шүүнэ. Кодын бүтэц шатлалтай (цех 2 орон ·
       хэлтэс 4 · хэсэг 6) тул УГТВАРААР тулгана — яг тэнцүүлбэл доод
       нэгжийн ажлууд шүүгдэж хаягдана. */
    if (state.section) {
      rows = rows.filter((j) =>
        isUnderUnit(j.negj_code || j.department_code, state.section),
      );
    }

    if (statusFilter === "approved")
      rows = rows.filter((j) => j.approve_id === 41);
    else if (statusFilter === "pending")
      rows = rows.filter((j) => j.approve_id == null);
    else if (statusFilter === "rejected")
      rows = rows.filter((j) => j.approve_id != null && j.approve_id !== 41);

    // Шатны шүүлт — аль алхам дээр гацсанаар нь
    if (stageFilter) rows = rows.filter((j) => j.stage?.stage === stageFilter);

    // Хэрэглэгчийн шүүлт
    if (userFilter === "mine" && myKey) {
      rows = rows.filter((j) => (j.stage?.actor_tns || []).includes(myKey));
    } else if (userFilter === "waiting") {
      rows = rows.filter(waitingForMe);
    }

    // Тодорхой хүнээр — §4-ийн нэр эсвэл ТН-аар
    const q = String(personQ || "").trim().toLowerCase();
    if (q) {
      const qTn = tnKey(q);
      rows = rows.filter((j) => {
        const st = j.stage;
        if (!st) return false;
        if (qTn && (st.actor_tns || []).includes(qTn)) return true;
        return Object.values(st.parties || {}).some((p) =>
          (p.names || []).some((n) => String(n).toLowerCase().includes(q)),
        );
      });
    }

    /* ⚠ ГРУППЛЭХ ТҮЛХҮҮРИЙГ ЭНД БЭЛДЭНЭ. PrimeReact-ийн `groupRowsBy` нь
       мөрийн ТАЛБАРЫГ л уншиж чадна (функц дэмждэггүй) тул БН-ийн кодыг
       `__bn` болгон хадгална. Эрэмбэ ба групплэлт ИЖИЛ талбараар явах
       ёстой — эс бөгөөс нэг БН хэд хэдэн удаа давтагдаж гарна. */
    return rows.map((j) => ({
      ...j,
      __bn: bnKeyOf(j.tseh_code || j.department_code) || "??",
    }));
  }, [
    list,
    state.section,
    statusFilter,
    stageFilter,
    userFilter,
    personQ,
    myKey,
    tnKey,
    waitingForMe,
  ]);

  /* ── БН (ЦЕХ)-ЭЭР ГРУППЛЭНЭ — КОДООР ────────────────────────────────
     ⚠ Өмнө нь `department_name`-ээр бүлэглэдэг байсан нь ХЭСЭГ/НЭГЖ
       түвшин — нэг цехийн ажлууд 5-6 бүлэгт тарж, «БН-ээр групплэсэн»
       гэсэн бусад жагсаалттай зөрдөг байв (2026-09-26).
     ⚠ НЭРЭЭР бүлэглэхгүй: нэг нэгж хэд хэдэн нэрээр бичигддэг («0107»
       → «ХАБЭА хэлтэс» ба «Хөдөлмөрийн аюулгүй байдал…хэлтэс») тул
       нэрийн зөрүү бүлгийг хуваана. Групплэлт КОДООР (`bn_key`), нэр нь
       зөвхөн ХАРУУЛАХАД.
     ⚠ `tseh_code` хоосон хуучин мөр бий — тэднийг `department_code`-оос
       тайлна (backend ч мөн уншихдаа нөхдөг: Utils/unit_split.py). */
  const bnKey = React.useCallback(
    (j) => bnKeyOf(j?.tseh_code || j?.department_code) || "??",
    [],
  );

  // БН бүрийн НЭР + ажил/ажилчдын тоо — групп толгойд харуулна
  const groupCounts = React.useMemo(() => {
    const m = {};
    (filteredList || []).forEach((j) => {
      const k = bnKey(j);
      if (!m[k]) m[k] = { jobs: 0, workers: 0, name: "" };
      m[k].jobs += 1;
      m[k].workers += j.worker_count ?? (j.workers || []).length;
      // Нэр нь БН-ийнх байх ёстой. `department_name` нь ХЭСГИЙН нэр байж
      // болох тул тэрийг зөвхөн хамгийн сүүлчийн нөөц болгоно.
      if (!m[k].name && j.tseh_name) m[k].name = String(j.tseh_name).trim();
      if (!m[k].name && !isSectionCode(j.department_code) && j.department_name)
        m[k].name = String(j.department_name).trim();
    });
    return m;
  }, [filteredList, bnKey]);

  // Групп толгой — БН-ийн код · нэр + ажлын/ажилчдын тоо
  const groupHeaderTemplate = (data) => {
    const k = bnKey(data);
    const c = groupCounts[k] || { jobs: 0, workers: 0, name: "" };
    return (
      <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-emerald-700">
        <i className="fas fa-sitemap text-[11px]" />
        {c.name || `Нэгж ${k}`}
        <span className="text-[10px] font-medium text-gray-400">{k}</span>
        <span className="text-[10px] font-medium text-gray-500">
          · {c.jobs} ажил · {c.workers} ажилтан
        </span>
      </span>
    );
  };

  // Admin бол subheader группчилол; бусад хэрэглэгчид олон баганаар эрэмбэлнэ
  const groupProps = admin
    ? {
        rowGroupMode: "subheader",
        // ⚠ ЭРЭМБЭ ба ГРУППЛЭХ талбар ИЖИЛ байх ёстой — PrimeReact нь
        //   эрэмбэлэгдсэн дарааллаар бүлэглэдэг тул зөрвөл нэг БН хэд
        //   хэдэн удаа давтагдаж гарна.
        // ⚠ Групплэх/эрэмбэлэх талбар нь ХАРАГДДАГ багана БИШ, тусгайлан
        //   бэлдсэн `__bn` (БН-ийн код) — нэрийн зөрүүнээс хамаарахгүй.
        groupRowsBy: "__bn",
        sortMode: "single",
        sortField: "__bn",
        sortOrder: 1,
        rowGroupHeaderTemplate: groupHeaderTemplate,
      }
    : { sortMode: "multiple" };

  // Ажилтны 15А төлөв — өнгөт badge
  /**
   * ДУГААРЫН ДОР гарах тэмдэг — «аюулын жагсаалтаас хуваарилагдсан».
   *
   * ⚠ Наряд нь ХАБ хэлтсээс ирсэн аюулын мөрөөс үүссэн эсэхийг жагсаалтаас
   *   ялгах боломжгүй байв. Төрлийг нь ч заана: НЗ = наряд зөвшөөрөл (ӨЭА),
   *   НД = ажлын даалгавар (хуваарилахдаа сонгосон төрөл).
   */
  const assignedHazardMark = (job) => {
    const list = job?.assigned_hazards || [];
    if (!list.length) return null;
    const isOea =
      String(list[0].assigned_work_type || "OEA").toUpperCase() === "OEA";
    return (
      <span
        title={
          "Аюулын жагсаалтаас хуваарилагдсан — " +
          (isOea ? "наряд зөвшөөрөл (ӨЭА)" : "ажлын даалгавар") +
          "\n" +
          list.map((h) => `${h.report_number} · ${h.description || ""}`).join("\n")
        }
        className={
          "inline-flex items-center gap-[2px] mt-[3px] px-[3px] rounded text-[9px] font-bold border cursor-help " +
          (isOea
            ? "bg-purple-50 border-purple-300 text-purple-700"
            : "bg-red-50 border-red-300 text-red-700")
        }
      >
        <i className="fas fa-exclamation-triangle text-[8px]" />
        {isOea ? "НЗ" : "НД"}
      </span>
    );
  };

  const tag15 = (w) =>
    w.checklist_15_completed ? (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
        Бөглөсөн{w.checklist_15_score ? ` (${w.checklist_15_score})` : ""}
      </span>
    ) : (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500">
        Бөглөөгүй
      </span>
    );

  // Аюул бүртгэгдсэн эсэх — өнгөт badge
  const tagHazard = (w) =>
    w.hazard_reported ? (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700">
        <i className="fas fa-exclamation-triangle mr-1" />
        {w.hazard_report_count || 1} аюул
      </span>
    ) : (
      <span className="text-gray-300">—</span>
    );

  // HTML injection-оос хамгаална — тайлбар нь хэрэглэгчийн бичсэн чөлөөт
  // текст тул Swal-ийн `html`-д шууд тавьж БОЛОХГҮЙ.
  const esc = (v) =>
    String(v ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const SEVERITY_COLOR = {
    1: "#16A34A",
    2: "#CA8A04",
    3: "#EA580C",
    4: "#DC2626",
    5: "#991B1B",
  };

  /** Ажилтны бүртгэсэн аюулуудын HTML — дэлгэрэнгүй цонхонд. */
  const hazardsHtml = (w) => {
    const list = Array.isArray(w.hazards) ? w.hazards : [];
    if (!w.hazard_reported && list.length === 0) {
      return '<span style="color:#16A34A">Илрээгүй</span>';
    }
    // Тоо нь бүртгэгдсэн ч дэлгэрэнгүй ирээгүй бол (хуучин бүртгэл эсвэл
    // устгагдсан) дор хаяж тоог нь харуулна — «алга» гэж ойлгогдохгүй.
    if (list.length === 0) {
      return `<span style="color:#DC2626">${
        w.hazard_report_count || 1
      } бүртгэгдсэн</span>`;
    }
    const rows = list
      .map((h) => {
        const c = SEVERITY_COLOR[h.severity] || "#6B7280";
        return `
        <div style="border-left:3px solid ${c};background:#FCFDFE;
                    padding:6px 10px;margin-top:6px;border-radius:0 6px 6px 0">
          <div style="font-size:11px;color:#8A94A6;font-family:monospace">
            ${esc(h.report_number || "")}
            ${h.report_date ? " · " + esc(String(h.report_date).slice(0, 10)) : ""}
          </div>
          <div style="font-size:13px;color:#1F2430;margin-top:2px">
            ${esc(h.description || "—")}
          </div>
          <div style="font-size:11px;color:${c};margin-top:2px">
            ${esc(h.severity_label || "")}
            ${h.hazard_category_name ? " · " + esc(h.hazard_category_name) : ""}
            ${h.location_text ? " · " + esc(h.location_text) : ""}
          </div>
        </div>`;
      })
      .join("");
    return `<span style="color:#DC2626">${list.length} бүртгэгдсэн</span>${rows}`;
  };

  /** Биоритм — ЗӨВХӨН эрхтэй хэрэглэгчид (backend `null` буцаавал нуугдана). */
  const bioHtml = (w) => {
    const b = w.biorhythm;
    if (!b || !b.cycles) return "";
    const color =
      b.level === "critical" ? "#DC2626" : b.level === "low" ? "#CA8A04" : "#16A34A";
    const bars = Object.values(b.cycles)
      .map((cy) => {
        const pct = Number(cy.percent || 0);
        const cc =
          cy.state === "critical" ? "#DC2626" : cy.state === "low" ? "#CA8A04" : "#16A34A";
        return `
        <div style="display:flex;align-items:center;gap:8px;margin-top:3px">
          <span style="width:96px;font-size:11px;color:#5A6478">${esc(cy.label)}</span>
          <span style="flex:1;height:6px;background:#EDEFF3;border-radius:3px;
                       position:relative;overflow:hidden;display:inline-block">
            <span style="position:absolute;left:50%;top:0;height:6px;
                         width:${Math.abs(pct) / 2}%;background:${cc};
                         ${pct < 0 ? "transform:translateX(-100%)" : ""}"></span>
          </span>
          <span style="width:44px;text-align:right;font-size:11px;color:${cc};
                       font-weight:700">${pct}%</span>
        </div>`;
      })
      .join("");
    return `
      <div style="margin-top:10px;padding-top:8px;border-top:1px solid #EDEFF3">
        <b>Биоритм:</b>
        <span style="color:${color};font-weight:700">
          ${b.level === "critical" ? "Эгзэгтэй" : b.level === "low" ? "Бага" : "Хэвийн"}
        </span>
        ${bars}
        <div style="font-size:10.5px;color:#9AA3B0;margin-top:6px;line-height:1.4">
          ⚠ Зөвхөн сэрэмжлүүлэг. Ажилд гаргах/гаргахгүй шийдвэрийн шалгуур БИШ
          (шинжлэх ухааны баталгаагүй).
        </div>
      </div>`;
  };

  // "Харах" — ажилтны дэлгэрэнгүйг харуулна
  const viewWorker = (w) => {
    Swal.fire({
      title: `${w.worker_name || ""} — ${w.position || ""}`,
      icon: "info",
      width: 620,
      html: `
        <div style="text-align:left;font-size:13px;line-height:1.7">
          <b>ТН:</b> ${esc(w.tn || "-")}<br/>
          <b>Огноо:</b> ${w.work_date ? moment(w.work_date).format("YYYY-MM-DD") : "-"}<br/>
          <b>Очих газар:</b> ${esc(w.destination || "-")}<br/>
          <b>15А шалгалт:</b> ${
            w.checklist_15_completed
              ? "Бөглөсөн" + (w.checklist_15_score ? ` (${w.checklist_15_score})` : "")
              : "Бөглөөгүй"
          }<br/>
          <b>Аюул:</b> ${hazardsHtml(w)}
          ${bioHtml(w)}
        </div>`,
      confirmButtonText: "Хаах",
      confirmButtonColor: "#0F6E56",
    });
  };

  // Задардаг мөр — тухайн ажилд бүртгэгдсэн ажилчдын хүснэгт
  const rowExpansionTemplate = (data) => {
    const workers = data.workers || [];
    return (
      <div className="p-3 bg-slate-50 rounded-md" style={{ width: "100%" }}>
        <div className="font-semibold text-xs mb-2 text-slate-600">
          <i className="fas fa-users mr-1" />
          Бүртгэгдсэн ажилчид ({workers.length})
        </div>
        {workers.length === 0 ? (
          <div className="text-xs text-gray-400 italic py-2">
            Энэ ажилд ажилтан бүртгэгдээгүй байна.
          </div>
        ) : (
          <DataTable
            value={workers}
            size="small"
            dataKey="id"
            className="table-xs"
            showGridlines
            style={{ width: "100%" }}
            tableStyle={{ width: "100%", minWidth: "100%" }}
          >
            <Column
              header="№"
              body={(d, r) => r.rowIndex + 1}
              style={{ width: "40px" }}
              align="center"
            />
            <Column
              header="Нэр"
              field="worker_name"
              className="text-xs"
              style={{ minWidth: "160px" }}
              body={(w) => (
                <span className="inline-flex items-center gap-1">
                  {w.worker_name}
                  {/* ⚠ Биоритм нь ШИНЖЛЭХ УХААНЫ БАТАЛГААГҮЙ. Ажлаас хасах
                      шалгуур БИШ — зөвхөн «өнөөдөр болгоомжтой байлгаарай»
                      гэсэн сэрэмжлүүлэг. Эрсдэлгүй өдөр тэмдэг гарахгүй. */}
                  <BiorhythmMark bio={w.biorhythm} />
                </span>
              )}
            />
            <Column
              header="Албан тушаал"
              field="position"
              className="text-xs"
              style={{ minWidth: "180px" }}
            />
            <Column
              header="Огноо"
              className="text-xs"
              body={(w) =>
                w.work_date ? moment(w.work_date).format("YYYY-MM-DD") : "-"
              }
            />
            <Column
              header="15А төлөв"
              align="center"
              body={(w) => tag15(w)}
            />
            <Column header="Аюул" align="center" body={(w) => tagHazard(w)} />
            <Column
              header=""
              align="center"
              style={{ width: "70px" }}
              body={(w) => (
                <button
                  title="Харах"
                  className="p-1 flex items-center justify-center font-semibold text-green-600 rounded-full border-2 border-green-600 hover:bg-green-500 hover:scale-110 hover:text-white focus:outline-none duration-300"
                  onClick={() => viewWorker(w)}
                >
                  <i className="fe fe-chevrons-right" />
                </button>
              )}
            />
          </DataTable>
        )}
      </div>
    );
  };

  return (
    <div className=" card flex  p-2 border rounded text-xs">
      {" "}
      <Modal
        style={{ width: "600" }}
        width={800}
        height={550}
        open={state.register.modal}
        onCancel={() =>
          dispatch({
            type: "STATE",
            data: { register: { ...state.register, modal: false } },
          })
        }
        title={"Бүртгэл"}
        closeIcon={<div className="">x</div>}
        footer={false}
      >
        <MODAL />
      </Modal>
      <Modal
        style={{ width: "600" }}
        width={800}
        height={550}
        open={state.register.modal_approve}
        onCancel={() =>
          dispatch({
            type: "STATE",
            data: { register: { ...state.register, modal_approve: false } },
          })
        }
        title={"Өндөр эрсдэлтэй ажлын жагсаалтад оруулж баталгаажуулах"}
        closeIcon={<div className="">x</div>}
        footer={false}
      >
        <MODAL_APPROVE />
      </Modal>

      {/* ⚠ ДАРААЛАЛ: ХАЙЛТ → ХЯНАЛТ → ЖАГСААЛТ (2026-09-26).
          Хяналтын тоонууд нь хайлтын үр дүнгээс бодогддог тул шүүлт нь
          ДЭЭР байх ёстой — эс бөгөөс хэрэглэгч тоог хараад дараа нь
          шүүлтээ олохоор доош гүйлгэх шаардлагатай болно. */}
      {/* ── Шүүлтийн хэсэг ── */}
      <div className="flex gap-2 flex-wrap items-center mb-2" style={{ width: "100%" }}>
        <SearchInput
          className="w-full md:w-72"
          size="small"
          placeholder="Ажил / нэгжээр хайх..."
          value={search.global.value}
          onChange={(v) => {
            // ⚠ global.value-г ШУУД өөрчилдөг байв. { ...search } нь
            //   гүехэн хуулбар тул `global` нь эх объектоо заасаар
            //   байдаг — өөрөөр хэлбэл React төлөв мутацилагдана.
            setSearch({ ...search, global: { ...search.global, value: v } });
          }}
        />
        <Select
          size="small"
          style={{ minWidth: 170 }}
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: "", label: "Бүх төлөв" },
            { value: "approved", label: "Зөвшөөрсөн" },
            { value: "pending", label: "Хүлээгдэж буй" },
            { value: "rejected", label: "Татгалзсан" },
          ]}
        />

        {/* ── ХЭРЭГЛЭГЧТЭЙ ХОЛБООТОЙ ШҮҮЛТ ──────────────────────────────
            ХАБЭА-н хяналтын инженер олон наряд хянахдаа «аль нь надаас
            хамаарч гацсан бэ» гэдгийг олох боломжгүй байв. */}
        <Select
          size="small"
          style={{ minWidth: 200 }}
          value={userFilter}
          onChange={setUserFilter}
          options={[
            { value: "", label: "Бүх хэрэглэгч" },
            { value: "mine", label: "Миний оролцсон" },
            { value: "waiting", label: "Миний гарын үсэг хүлээгдэж буй" },
          ]}
        />
        <Select
          size="small"
          allowClear
          style={{ minWidth: 190 }}
          value={stageFilter || undefined}
          onChange={(v) => setStageFilter(v || "")}
          placeholder="Аль шатанд гацсан"
          options={[
            { value: "parties", label: "§4 ИТА томилоогүй" },
            { value: "signed", label: "§4 Гарын үсэг дутуу" },
            { value: "open", label: "§5 Ээлж нээгээгүй" },
            { value: "close", label: "§6 Ээлж хаагаагүй" },
            { value: "done", label: "Бүрэн дууссан" },
          ]}
        />
        <SearchInput
          className="w-full md:w-56"
          size="small"
          placeholder="ИТА-гийн нэр / Б/Д-аар"
          value={personQ}
          onChange={setPersonQ}
        />
        {statusFilter || userFilter || stageFilter || personQ ? (
          <Tooltip title="Шүүлт цэвэрлэх">
            <Button
              size="small"
              type="text"
              onClick={() => {
                setStatusFilter("");
                setUserFilter("");
                setStageFilter("");
                setPersonQ("");
              }}
            >
              <i className="fas fa-filter-circle-xmark" />
            </Button>
          </Tooltip>
        ) : null}
      </div>

      {/* ── Хяналтын самбар (KPI) ── */}
      <div
        className="flex gap-2 flex-wrap mb-2"
        style={{ width: "100%" }}
      >
        <KpiTile
          label="Нийт ажил"
          value={kpi.jobs}
          color="#0F6E56"
          icon="fa-briefcase"
        />
        <KpiTile
          label="Ажилчид"
          value={kpi.workers}
          color="#2563EB"
          icon="fa-users"
        />
        <KpiTile
          label="15А бөглөлт"
          value={`${kpi.pct15}%`}
          sub={`${kpi.done15}/${kpi.workers}`}
          color={kpi.pct15 < 50 ? "#DC2626" : kpi.pct15 < 80 ? "#D97706" : "#16A34A"}
          icon="fa-clipboard-check"
        />
        <KpiTile
          label="Аюул илэрсэн"
          value={kpi.hazardWorkers}
          color="#DC2626"
          icon="fa-exclamation-triangle"
        />

        {/* ── ШАТНЫ ЯВЦААС ────────────────────────────────────────────
            ⚠ `stage` талбаргүй (migration ажиллаагүй) үед эдгээрийг
              ОГТ харуулахгүй. «0» гэж үзүүлбэл бүх наряд эрүүл мэт
              ХУДАЛ дүр зураг гарна. */}
        {stageKpi.has ? (
          <>
            <KpiTile
              label="Дундаж явц"
              value={`${stageKpi.avg}%`}
              sub={`${stageKpi.total} наряд`}
              color={
                stageKpi.avg < 50
                  ? "#DC2626"
                  : stageKpi.avg < 80
                    ? "#D97706"
                    : "#16A34A"
              }
              icon="fa-tachometer-alt"
            />
            <KpiTile
              label="Бүрэн дууссан"
              value={stageKpi.done}
              sub={`${stageKpi.total}-аас`}
              color="#16A34A"
              icon="fa-check-circle"
              active={stageFilter === "done"}
              onClick={() =>
                setStageFilter(stageFilter === "done" ? "" : "done")
              }
            />
            <KpiTile
              label="§4 гарын үсэг дутуу"
              value={stageKpi.signWait}
              color="#D97706"
              icon="fa-signature"
              active={stageFilter === "signed"}
              onClick={() =>
                setStageFilter(stageFilter === "signed" ? "" : "signed")
              }
            />
            <KpiTile
              label="Ээлж хаагаагүй"
              value={stageKpi.shiftOpen}
              sub="одоо ажиллаж буй"
              color="#EA580C"
              icon="fa-person-digging"
              active={stageFilter === "close"}
              onClick={() =>
                setStageFilter(stageFilter === "close" ? "" : "close")
              }
            />
            <KpiTile
              label="Хугацаа хэтэрсэн"
              value={stageKpi.overdue}
              color="#B91C1C"
              icon="fa-hourglass-end"
            />
            {/* ⚠ ЗӨВХӨН өөрөөс хамаарч гацсан наряд байвал харуулна —
                тэг тоо нь бусад картыг шахаж, анхаарлыг сарниулна. */}
            {stageKpi.mineWait > 0 ? (
              <KpiTile
                label="Миний гарын үсэг хүлээгдэж буй"
                value={stageKpi.mineWait}
                color="#7C3AED"
                icon="fa-user-clock"
                active={userFilter === "waiting"}
                onClick={() =>
                  setUserFilter(userFilter === "waiting" ? "" : "waiting")
                }
              />
            ) : null}
          </>
        ) : null}
      </div>

      <DataTable
        size="small"
        value={filteredList}
        filters={search}
        expandedRows={expandedRows}
        /* ⚠ PrimeReact 8.7.3-ын АЛДААГ тойрч гарна — `dataKey`-г ОГТ
           ӨГӨХГҮЙ, төлөвийг МАССИВ хэлбэрээр барина.

           datatable.cjs.js дотор хоёр функц ЗӨРНӨ:
             onRowToggle:   hasDataKey = groupRowsBy ? dataKey === groupRowsBy
                                                     : !!dataKey
                            → группчилсэн үед (админ) dataKey("id") ≠
                              groupRowsBy тул МАССИВ салаа руу орж
                              `[...props.expandedRows]` гэж задална.
                              ОБЪЕКТ дамжуулбал ЭНД ШУУД УНАНА
                              («not iterable») — сум дарахад юу ч болохгүй.
             isRowExpanded: `dataKey` байвал `expandedRows[id]` гэж ОБЪЕКТ
                            гэж уншина.
           Хоёулаа зэрэг хангагдахгүй тул `dataKey`-г хасав: тэгвэл хоёр
           функц хоёулаа МАССИВ дээр `equals()`-ээр зөв ажиллана.

           ⚠ Өмнөх «объект болгож жигдэлнэ» тойрог нь ЭХНИЙ товшилтод л
             ажиллаж, хоёр дахьд нь объектыг задлах гэж унадаг байв. */
        onRowToggle={(e) =>
          setExpandedRows(
            Array.isArray(e.data) ? e.data : Object.values(e.data || {}),
          )
        }
        rowExpansionTemplate={rowExpansionTemplate}
        paginator
        scrollable
        removableSort
        rowHover
        showGridlines
        className="table-xs"
        filterDisplay="menu"
        responsiveLayout="scroll"
        {...groupProps}
        scrollHeight={window.innerHeight - 360}
        globalFilterFields={["tn", "short_name", "position_namemn"]}
        emptyMessage={
          <div className="text-xs text-orange-500 italic font-semibold">
            Мэдээлэл олдсонгүй...
          </div>
        }
        header={
          // ⚠ Энд өмнө нь ХОЁР ДАХЬ хайлтын талбар байсан — дээрх шүүлтийн
          //   мөрийнхтэй ЯГ НЭГ `search.global.value` төлөвт холбогдсон тул
          //   хуудсан дээр хоёр ижил хайлт харагддаг байв. Шүүлт нэг дор
          //   (төлөвийн сонголттой хамт) байх нь зөв тул эндхийг авав.
          <div className="flex flex-wrap items-center justify-end gap-2 border-b pb-2 mb-2  text-xs">
            <div className="flex items-center gap-3">
              {/* {checkRole(["inspection_register_add"]) && ( */}
              <>
                <div
                  title="Нэмэх"
                  className="p-1 flex items-center justify-center font-semibold text-violet-500 border-2 border-violet-500 rounded-full hover:bg-violet-500 hover:text-white hover:scale-125 focus:outline-none duration-300 cursor-pointer mr-1"
                  onClick={() => {
                    // Шинээр нэмэх — өмнөх засварын утгуудаас сэргийлж бүх
                    // register талбарыг анхдагч руу resetlана.
                    dispatch({
                      type: "STATE",
                      data: {
                        register: {
                          modal: true,
                          modal_approve: false,
                          id: 0,
                          yearc: moment(),
                          type_id: 0,
                          // ТТ/карт/зааврыг ЗААВАЛ цэвэрлэнэ — эс бөгөөс
                          // өмнөх нарядын сонголт шинэ рүү дамжина
                          hr: null,
                          tseh_code: null,
                          tseh_name: null,
                          negj_code: null,
                          negj_name: null,
                          department: null,
                          department_code: null,
                          department_name: null,
                          jobdesc: null,
                          comment: null,
                          start_date: null,
                          end_date: null,
                          selected_consequence: null,
                          selected_probability: null,
                          matrix_score: null,
                          matrix_grade: null,
                          matrix_id: 0,
                          approve_type_id: 0,
                          approve_comment: null,
                          readonly: false,
                        },
                      },
                    });
                  }}
                >
                  <i className="fa fa-plus" />
                </div>
              </>
              {/* )} */}
            </div>
          </div>
        }
        // rowClassName={(data) => {
        //   var result = "cursor-pointer";
        //   if (state.id === data.id) result = " bg-blue-500 text-white";
        //   return result;
        // }}
        // onRowClick={(e) => {
        //   dispatch({ type: "STATE", data: { id: e.data.id } });
        // }}
        rows={per_page}
        first={first}
        onPage={(event) => {
          set_first(event.first);
          set_per_page(event.rows);
        }}
        paginatorTemplate={{
          layout:
            "RowsPerPageDropdown CurrentPageReport PrevPageLink NextPageLink",
          RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
              { label: 50, value: 50 },
              { label: 100, value: 100 },
              { label: 200, value: 200 },
              { label: 500, value: 500 },
            ];
            return (
              <>
                <span
                  className="text-xs mx-1"
                  style={{
                    color: "var(--text-color)",
                    userSelect: "none",
                  }}
                >
                  <span className="font-semibold">Нэг хуудсанд:</span>
                </span>
                <Select
                  size="small"
                  showSearch={false}
                  value={options.value}
                  onChange={(value) => {
                    options.onChange({
                      value: value,
                    });
                  }}
                >
                  {_.map(dropdownOptions, (item) => (
                    <Select.Option key={item.value} value={item.value}>
                      {item.value}
                    </Select.Option>
                  ))}
                </Select>
              </>
            );
          },
          CurrentPageReport: (options) => {
            return (
              <span
                style={{
                  color: "var(--text-color)",
                  userSelect: "none",
                  width: "200px",
                  textAlign: "center",
                }}
              >
                <span className="text-xs font-semibold">
                  <span>
                    {options.first} - {options.last}
                  </span>
                  <span className="ml-3">Нийт: {options.totalRecords}</span>
                </span>
              </span>
            );
          },
        }}
        paginatorClassName="justify-content-end"
      >
        <Column expander style={{ width: "3rem" }} />
        <Column
          header="№"
          align="center"
          className="text-xs"
          style={{ minWidth: "34px", maxWidth: "34px" }}
          body={(data, row) => (
            <div className="leading-tight">
              <div>{row.rowIndex + 1}</div>
              {/* Аюулын жагсаалтаас хуваарилагдаж ирсэн наряд — дугаарын
                  дор жижиг тэмдэг. НЗ = наряд зөвшөөрөл (ӨЭА),
                  НД = ажлын даалгавар. */}
              {assignedHazardMark(data)}
            </div>
          )}
        />

        {!admin && (
          <Column
            header="Бүтцийн нэгж"
            field="department_name"
            className="text-xs"
            style={{ minWidth: "200px", maxWidth: "200px" }}
          />
        )}
        {/* Ажлын төрөл — өмнө нь бүртгэгддэггүй байсан (type_id үргэлж 0) */}
        <Column
          header="Ажлын төрөл"
          align="center"
          className="text-xs"
          style={{ minWidth: "150px", maxWidth: "180px" }}
          body={(data) => {
            // Олон төрөл — бүгдийг нь; эхнийх нь үндсэн тул тодоор
            const names =
              (data.type_names || []).length
                ? data.type_names
                : data.reference_name
                ? [data.reference_name]
                : [];
            return names.length ? (
              <span className="whitespace-normal break-words">
                {names.map((n, i) => (
                  <span key={n}>
                    {i === 0 ? <b>{n}</b> : n}
                    {i < names.length - 1 ? " · " : ""}
                  </span>
                ))}
              </span>
            ) : (
              <span className="text-gray-300">—</span>
            );
          }
          }
        />
        {/* Гүйцэтгэгч — дотоод нэгж эсвэл гэрээт байгууллага */}
        <Column
          header="Гүйцэтгэгч"
          className="text-xs"
          style={{ minWidth: "140px", maxWidth: "170px" }}
          body={(data) => {
            const ext = data.executor_type === "external";
            const name = ext
              ? data.executor_org_name
              : data.executor_name || data.executor_section_name;
            if (!name) return <span className="text-gray-300">—</span>;
            return (
              <span className="whitespace-normal break-words">
                {ext && (
                  <span
                    className="mr-1 px-1 rounded bg-amber-100 text-amber-700 text-[10px] font-bold"
                    title="Гэрээт гүйцэтгэгч байгууллага"
                  >
                    ГЭРЭЭТ
                  </span>
                )}
                {name}
              </span>
            );
          }}
        />
        <Column
          header="Ажилчид"
          align="center"
          className="text-xs"
          style={{ minWidth: "90px", maxWidth: "110px" }}
          body={(data) => {
            // БОДИТ = registerTn дээр бүртгэгдсэн · ТӨЛӨВЛӨСӨН = нарядад заасан
            const actual =
              data.actual_worker_count ??
              data.worker_count ??
              (data.workers || []).length;
            const planned = data.planned_worker_count;
            const done = data.checklist_done_count ?? 0;
            const short = planned != null && actual < planned;
            return (
              <div className="flex flex-col items-center gap-0.5">
                <span
                  className={
                    "px-2 py-0.5 rounded-full text-[11px] font-bold " +
                    (short
                      ? "bg-amber-100 text-amber-700"
                      : actual > 0
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-gray-100 text-gray-400")
                  }
                  title={
                    planned != null
                      ? `Төлөвлөсөн ${planned} · бүртгэгдсэн ${actual}`
                      : "Бүртгэгдсэн ажилчдын тоо"
                  }
                >
                  <i className="fas fa-users mr-1" />
                  {actual}
                  {planned != null && (
                    <span className="font-normal opacity-70">/{planned}</span>
                  )}
                </span>
                {actual > 0 && (
                  <span
                    className={
                      "text-[10px] " +
                      (done < actual ? "text-red-500" : "text-emerald-600")
                    }
                    title="15 критик асуумжаа бөглөсөн ажилчид"
                  >
                    15 асуумж: {done}/{actual}
                  </span>
                )}
              </div>
            );
          }}
        />
        {/* ── ШАТ ШАТНЫ ЯВЦ ────────────────────────────────────────────
            §4 ИТА → §5 ээлж нээх → §6 ээлж хаах. Хэн ямар шатанд юу
            хийсэн, одоо ХЭНИЙГ хүлээж байгааг жагсаалтаас шууд харна. */}
        <Column
          header="Шатны явц"
          className="text-xs"
          style={{ minWidth: "230px", maxWidth: "280px" }}
          body={(data) => {
            const st = data.stage;
            // ⚠ Хүснэгт үүсээгүй (migration ажиллаагүй) бол backend энэ
            //   талбарыг огт өгөхгүй. «0%» гэж харуулбал наряд огт
            //   хийгдээгүй мэт ХУДАЛ дүр зураг гарна.
            if (!st) return <span className="text-gray-300">—</span>;

            const pct = st.percent ?? 0;
            const done = st.stage === "done";
            // Явцын зурвасны өнгө — дууссан ногоон, дундуур цэнхэр,
            // эхэн үе ТОД шар (цайвар шар нь цагаан дэвсгэр дээр
            // ялгарахгүй байсан).
            const barColor = done ? "#16A34A" : pct >= 50 ? "#0EA5E9" : "#F59E0B";

            /** Дүр бүр ТУСДАА МӨР — зүүн талд өнгөт зурвас, баруун талд
             *  «зурсан/нийт» тэмдэг. Нэр нь урт бол ДООШОО буулгана
             *  (`whiteSpace: normal`) — өмнө нь таслагдаж, аль дүр
             *  гацсаныг ялгах боломжгүй байв. */
            const partyRow = (key) => {
              const p = st.parties?.[key];
              if (!p || !p.count) return null;
              const tone = PARTY_TONE[key] || { bar: "#94A3B8" };
              const full = p.signed >= p.count;
              const chip = full ? SIGN_DONE : SIGN_WAIT;
              return (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 5,
                    padding: "1px 0",
                  }}
                >
                  {/* Дүрийн өнгөт зурвас — §4 доторх өнгөтэй ижил */}
                  <span
                    style={{
                      width: 3,
                      alignSelf: "stretch",
                      minHeight: 13,
                      background: tone.bar,
                      borderRadius: 2,
                      flex: "0 0 auto",
                    }}
                  />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 10.5,
                      lineHeight: 1.25,
                      color: "#475569",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {tone.label || p.label}
                  </span>
                  <span
                    style={{
                      flex: "0 0 auto",
                      fontSize: 9.5,
                      fontWeight: 700,
                      padding: "0 4px",
                      borderRadius: 7,
                      background: chip.bg,
                      color: chip.fg,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {p.signed}/{p.count}
                  </span>
                </div>
              );
            };

            const sh = st.shifts || {};
            const shiftWait = (sh.opened || 0) > (sh.closed || 0);

            return (
              <Tooltip
                title={
                  <div style={{ fontSize: 11.5 }}>
                    <div style={{ fontWeight: 700, marginBottom: 3 }}>
                      {st.stage_label}
                    </div>
                    {PARTY_SEQ.map((k) => {
                      const p = st.parties?.[k];
                      if (!p || !p.count) return null;
                      return (
                        <div key={k}>
                          {p.label}: {p.names.join(", ") || "—"} (гарын үсэг{" "}
                          {p.signed}/{p.count})
                        </div>
                      );
                    })}
                    <div style={{ marginTop: 3 }}>
                      Ээлж: нээсэн {sh.opened ?? 0} · хаасан {sh.closed ?? 0} /
                      нийт {sh.total ?? 0}
                    </div>
                    {st.waiting_for?.length ? (
                      <div style={{ marginTop: 3, color: "#FCD34D" }}>
                        Хүлээгдэж буй: {st.waiting_for.join(", ")}
                      </div>
                    ) : null}
                  </div>
                }
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    padding: "2px 2px",
                    textAlign: "left",
                  }}
                >
                  {/* Шатны нэр — ДҮҮРГЭСЭН шошго болгов. Зөвхөн үсгийн
                      өнгөөр бичихэд шар нь цагаан дэвсгэр дээр
                      ялгарахгүй байв. */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      justifyContent: "space-between",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 9.5,
                        fontWeight: 700,
                        padding: "1px 6px",
                        borderRadius: 8,
                        background: done ? SIGN_DONE.bg : SIGN_WAIT.bg,
                        color: done ? SIGN_DONE.fg : SIGN_WAIT.fg,
                        whiteSpace: "normal",
                        lineHeight: 1.3,
                      }}
                    >
                      {st.stage_label}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: barColor,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {pct}%
                    </span>
                  </div>

                  <div
                    style={{
                      height: 4,
                      background: "#EEF1F5",
                      borderRadius: 3,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: "100%",
                        background: barColor,
                      }}
                    />
                  </div>

                  {/* Дүр бүр НЭГ МӨР */}
                  <div style={{ marginTop: 1 }}>
                    {PARTY_SEQ.map(partyRow)}
                  </div>

                  {/* §5/§6 ээлж — ХООСОН бол ч харуулна: наряд бичээд
                      ажил эхлээгүйг ялгах цорын ганц шинж. */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 9.5,
                      color: "#64748B",
                    }}
                  >
                    <i className="fas fa-clock" style={{ fontSize: 9 }} />
                    <span>
                      Ээлж: нээсэн {sh.opened ?? 0} · хаасан {sh.closed ?? 0}
                    </span>
                    {shiftWait ? (
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          padding: "0 4px",
                          borderRadius: 7,
                          background: SIGN_WAIT.bg,
                          color: SIGN_WAIT.fg,
                        }}
                      >
                        хаагаагүй
                      </span>
                    ) : null}
                  </div>

                  {/* ⚠ `truncate` ХАСАГДСАН. Хүлээгдэж буй дүрүүд нь
                      яг тэр мэдээлэл болохоор таслагдах ёсгүй —
                      «Ажил гүйцэтгэгч, Зөвшөө…» гэж тасрахад хэнийг
                      хүлээж байгаа нь тодорхойгүй үлдэнэ. */}
                  {st.waiting_for?.length ? (
                    <div
                      style={{
                        fontSize: 9.5,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        padding: "2px 5px",
                        borderRadius: 6,
                        background: SIGN_WAIT.bg,
                        color: SIGN_WAIT.fg,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      ⏳ {st.waiting_for.join(", ")}
                    </div>
                  ) : null}
                </div>
              </Tooltip>
            );
          }}
        />

        <Column
          header="Хийж гүйцэтгэх ажил"
          field="jobdesc"
          className="text-xs"
        />
        <Column
          header="Ажлын хугацаа"
          align="center"
          className="text-xs"
          style={{ minWidth: "150px", maxWidth: "170px" }}
          body={(data) => {
            const s = data.start_date
              ? moment(data.start_date).format("YYYY-MM-DD")
              : null;
            const e = data.end_date
              ? moment(data.end_date).format("YYYY-MM-DD")
              : null;
            if (!s && !e) return <span className="text-gray-300">—</span>;
            return (
              <span className="whitespace-nowrap">
                {s || "…"} <span className="text-gray-400">→</span> {e || "…"}
              </span>
            );
          }}
        />
        <Column
          header="Эрсдлийн эрэмбэ"
          field="jobdesc"
          align="center"
          className="text-xs"
          style={{ minWidth: "80px", maxWidth: "80px" }}
          body={(data) => {
            return data.matrix[0].grade;
          }}
        />
        <Column
          header="Эрсдлийн түвшин"
          align="center"
          field="jobdesc"
          className="text-xs"
          style={{ minWidth: "80px", maxWidth: "80px" }}
          body={(data) => {
            return data.matrix[0].score;
          }}
        />
        {/* ── БҮРТГЭСЭН — хэн, хэзээ ─────────────────────────────────
            ⚠ Наряд хэн бүртгэснийг жагсаалтаас харах боломжгүй байсан тул
              «энэ наряд хаанаас гарав» гэсэн асуултад нэг бүрчлэн нээж
              хариулдаг байв. Огноо нь ажлын хугацаанаас ӨӨР: наряд ажил
              эхлэхээс өмнө бүртгэгддэг тул зөрүү нь бэлтгэлийн хугацааг
              харуулна. */}
        <Column
          header="Бүртгэсэн"
          align="center"
          className="text-xs"
          style={{ minWidth: "120px", maxWidth: "130px" }}
          sortable
          field="insertdate"
          body={(data) => {
            const who = (data.insertusername || "").trim();
            const when = data.insertdate
              ? moment(data.insertdate).format("YYYY-MM-DD HH:mm")
              : null;
            if (!who && !when) return <span className="text-gray-300">—</span>;
            return (
              <div style={{ lineHeight: 1.3 }}>
                {who ? (
                  <Tooltip title={data.insertuserposition || ""}>
                    <div style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                      {who}
                    </div>
                  </Tooltip>
                ) : null}
                {when ? (
                  <div style={{ fontSize: 10, color: "#8A94A6", whiteSpace: "nowrap" }}>
                    {when}
                  </div>
                ) : null}
              </div>
            );
          }}
        />

        <Column
          header="Баталгаажуулалт"
          field="status_percent"
          align="center"
          className="text-xs"
          style={{ minWidth: "150px", maxWidth: "150px" }}
          body={(data, row) => {
            return (
              <>
                {data.approve_id === null ? (
                  <button
                    title="Баталгаажуулах"
                    className="p-1 flex items-center justify-center font-semibold text-green-500 rounded-full border-2 border-green-500 hover:bg-green-500 hover:scale-125 hover:text-white focus:outline-none duration-300"
                    onClick={() => {
                      dispatch({
                        type: "STATE",
                        data: {
                          register: {
                            ...state.approve,
                            id: data.id,
                            modal_approve: true,
                          },
                        },
                      });
                    }}
                  >
                    <i className="fe fe-file" />
                  </button>
                ) : data.approve_id && data.approve_id === 41 ? (
                  <>
                    <i className="fe fe-check-circle text-xl text-green-600 pr-1" />{" "}
                    {data.approve_name}
                  </>
                ) : (
                  <>
                    <i className="fe fe-minus text-red-600 rounded-full border-3 border-red-600" />{" "}
                    {data.approve_name}
                  </>
                )}
              </>
            );
          }}
        />
        <Column
          align="center"
          header="Үйлдэл"
          className="text-xs"
          /* ⚠ 70px байсныг өргөтгөв. Энэ нүдэнд ГУРВАН товч багтана
             (Харах · Засах · Хэвлэх). Товч бүр ≈26px (p-1 + border-2 + icon)
             дээр нь зай ба нүдний padding нэмэгдэхэд ~110px хэрэгтэй болдог
             тул 70px-д багтахгүй, товчнууд доош мөр таслагдаж байв. */
          style={{ minWidth: "118px", maxWidth: "118px" }}
          headerClassName="flex items-center justify-center"
          body={(item) => {
            const m =
              Array.isArray(item.matrix) && item.matrix[0]
                ? item.matrix[0]
                : {};
            // Засах эрх: зөвхөн нэмсэн хэрэглэгч өөрөө, ХАРИН АШУА аль хэдийн
            // баталсан (approve_id === 41 = "Зөвшөөрсөн") бол засах боломжгүй.
            const isApproved = item.approve_id === 41;
            const canEdit =
              !isApproved &&
              String(user?.tn || "") === String(item.insertusertn || "");
            const openModal = (readonly) => {
              dispatch({
                type: "REGISTER",
                data: {
                  modal: true,
                  id: item.id,
                  yearc: item.yearc ? moment(String(item.yearc)) : moment(),
                  type_id: item.type_id || 0,
                  // Олон төрөл — сервер `type_ids` буцаадаг (байхгүй бол
                  // ганц type_id-гаас үүсгэсэн)
                  type_ids: item.type_ids || (item.type_id ? [item.type_id] : []),
                  // Хадгалсан ТТ/карт/заавар (backend `hr` талбар)
                  hr: item.hr || null,
                  department_code: item.department_code,
                  department_name: item.department_name,
                  // БН + ХЭСЭГ — сервер нь хуучин мөрд ч кодоос нөхөж
                  // илгээдэг (Utils/unit_split.py). Ирээгүй бол модал
                  // өөрөө `department_code`-оос тайлна.
                  tseh_code: item.tseh_code || null,
                  tseh_name: item.tseh_name || null,
                  tseh_parent_id: null,
                  negj_code: item.negj_code || null,
                  negj_name: item.negj_name || null,
                  jobdesc: item.jobdesc,
                  comment: item.comment,
                  start_date: item.start_date || null,
                  end_date: item.end_date || null,
                  matrix_id: item.matrix_id,
                  matrix_score: m.score || null,
                  matrix_grade: m.grade || null,
                  selected_consequence: null,
                  selected_probability: null,
                  readonly: readonly,
                },
              });
            };
            // `flex-nowrap` — нүд нарийсвал товчнууд доош УНАХГҮЙ, нэг
            // мөрөнд зэрэгцэж үлдэнэ. gap-1 (4px) нь gap-2-оос нягт тул
            // гурван товч тайван багтана.
            return (
              <div className="flex flex-nowrap items-center justify-center gap-1">
                {/* Харах товч — бүх хэрэглэгчид харагдана (readonly modal) */}
                <button
                  title="Харах"
                  className="p-1 shrink-0 flex items-center justify-center font-semibold text-green-600 rounded-full border-2 border-green-600 hover:bg-green-500 hover:scale-125 hover:text-white focus:outline-none duration-300"
                  onClick={() => openModal(true)}
                >
                  <i className="fe fe-chevrons-right" />
                </button>

                {/* Засах товч — зөвхөн нэмсэн хэрэглэгчид өөрт нь харагдана */}
                {canEdit && (
                  <button
                    title="Засах"
                    className="p-1 shrink-0 flex items-center justify-center font-semibold text-yellow-500 rounded-full border-2 border-yellow-500 hover:bg-yellow-500 hover:scale-125 hover:text-white focus:outline-none duration-300"
                    onClick={() => openModal(false)}
                  >
                    <i className="fe fe-edit" />
                  </button>
                )}

                {/* Зөвшөөрлийн хуудас хэвлэх — талбай дээр ЦААС хэрэгтэй.
                    Шинэ таб-д нээнэ: хэвлээд хаахад жагсаалт байрандаа
                    үлдэж, шүүлтүүр/хуудаслалт дахин ачаалагдахгүй. */}
                <button
                  title="Зөвшөөрлийн хуудас хэвлэх"
                  className="p-1 shrink-0 flex items-center justify-center font-semibold text-slate-600 rounded-full border-2 border-slate-500 hover:bg-slate-600 hover:scale-125 hover:text-white focus:outline-none duration-300"
                  onClick={() =>
                    window.open("/highrisk/print/" + item.id, "_blank")
                  }
                >
                  <i className="fas fa-print" />
                </button>
              </div>
            );
          }}
        />
      </DataTable>
    </div>
  );
};

export default React.memo(RegisterList);
